/**
 * useUsersTableState — filter/sort/paging state for the Admin Users table
 * (issue #1224), extracted from `routes/admin/users/index.tsx` so the route
 * file is composition-only (issue #1411).
 *
 * Filtering and sorting are client-side over the already-fetched `users`
 * list — see the Admin Users page history for why the REST endpoint wasn't
 * extended with server-side query params instead.
 */
import { useMemo, useState } from 'react';
import type { PlatformUser, UserRole } from '../types/users';
import type { Team } from '../graphql/types';
import {
  filterUsers,
  sortUsers,
  sortTeamsByName,
  type UserSortColumn,
  type SortDir,
} from '../lib/usersFilter';

const DEFAULT_USERS_PAGE_SIZE = 10;

export interface UseUsersTableStateResult {
  search: string;
  roleFilter: UserRole | '';
  teamFilter: string;
  sortCol: UserSortColumn | null;
  sortDir: SortDir;
  page: number;
  pageSize: number;
  pagedUsers: PlatformUser[];
  totalFilteredCount: number;
  teamFilterOptions: Team[];
  handleSearchChange: (value: string) => void;
  handleRoleFilterChange: (value: UserRole | '') => void;
  handleTeamFilterChange: (value: string) => void;
  handleSort: (col: UserSortColumn) => void;
  handlePageSizeChange: (newPageSize: number) => void;
  setPage: (page: number) => void;
}

/** Filter/sort/paging state + derived slices for the Admin Users table. */
export function useUsersTableState(
  users: PlatformUser[],
  allTeams: Team[],
  userTeamsMap: Map<string, Team[]>
): UseUsersTableStateResult {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [teamFilter, setTeamFilter] = useState('');
  // Default sort is newest-first (issue #1233) so a row created via the Add
  // User dialog always lands on page 1 — `null` (insertion/backend order)
  // gave no such guarantee once the row count exceeded one page.
  const [sortCol, setSortCol] = useState<UserSortColumn | null>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_USERS_PAGE_SIZE);

  const filteredUsers = useMemo(
    () => filterUsers(users, { search, roleFilter, teamFilter }, userTeamsMap),
    [users, search, roleFilter, teamFilter, userTeamsMap]
  );

  const sortedUsers = useMemo(
    () => (sortCol ? sortUsers(filteredUsers, sortCol, sortDir) : filteredUsers),
    [filteredUsers, sortCol, sortDir]
  );

  const totalFilteredCount = sortedUsers.length;
  const pageStart = (page - 1) * pageSize;
  const pagedUsers = sortedUsers.slice(pageStart, pageStart + pageSize);

  const teamFilterOptions = useMemo(() => sortTeamsByName(allTeams), [allTeams]);

  const handleSearchChange = (value: string): void => {
    setSearch(value);
    setPage(1);
  };

  const handleRoleFilterChange = (value: UserRole | ''): void => {
    setRoleFilter(value);
    setPage(1);
  };

  const handleTeamFilterChange = (value: string): void => {
    setTeamFilter(value);
    setPage(1);
  };

  const handleSort = (col: UserSortColumn): void => {
    if (col === sortCol) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    setPageSize(newPageSize);
    setPage(1);
  };

  return {
    search,
    roleFilter,
    teamFilter,
    sortCol,
    sortDir,
    page,
    pageSize,
    pagedUsers,
    totalFilteredCount,
    teamFilterOptions,
    handleSearchChange,
    handleRoleFilterChange,
    handleTeamFilterChange,
    handleSort,
    handlePageSizeChange,
    setPage,
  };
}
